// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ProgressBar } from '../components/UIComponents';
import { CheckCircle, XCircle, ChevronRight, RotateCcw, Home, Copy, Check, Linkedin, Twitter, MessageCircle, Award } from 'lucide-react';
import { toast } from 'sonner';
import CertificateModal from '../components/CertificateModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnimatedScore = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime; let animationFrame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / 1500, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));
      if (progress < 1) animationFrame = requestAnimationFrame(animate);
    };
    const timer = setTimeout(() => { animationFrame = requestAnimationFrame(animate); }, 300);
    return () => { clearTimeout(timer); cancelAnimationFrame(animationFrame); };
  }, [value]);
  return <span className="tabular-nums">{displayValue}%</span>;
};

const ShareButtons = ({ score, lesson }) => {
  const [copied, setCopied] = useState(false);
  const topic = lesson?.title || 'finanza personale';
  const shareText = `Ho appena completato il quiz su "${topic}" con ${score}% su SAPIENS! 🎯\nMake sense of your money → sapiens.finance`;

  const openWindow = (url) => window.open(url, '_blank', 'width=640,height=480,noopener,noreferrer');

  const shareLinkedIn = () => {
    openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://sapiens.finance')}&summary=${encodeURIComponent(shareText)}`);
  };

  const shareTwitter = () => {
    openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`);
  };

  const shareWhatsApp = () => {
    openWindow(`https://wa.me/?text=${encodeURIComponent(shareText)}`);
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success('Testo copiato!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Impossibile copiare il testo');
    }
  };

  const btnClass = "flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all duration-200 text-sm font-medium";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.3 }}
      className="mt-6 border-t border-white/[0.08] pt-6"
      data-testid="share-section"
    >
      <p className="text-sm text-[#888780] mb-4 text-center">Condividi il tuo risultato</p>
      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={shareLinkedIn} className={btnClass} data-testid="share-linkedin">
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </button>
        <button onClick={shareTwitter} className={btnClass} data-testid="share-twitter">
          <Twitter className="w-4 h-4" />
          X (Twitter)
        </button>
        <button onClick={shareWhatsApp} className={btnClass} data-testid="share-whatsapp">
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </button>
        <button onClick={copyText} className={btnClass} data-testid="share-copy">
          {copied ? <Check className="w-4 h-4 text-[#3D7A52]" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copiato!' : 'Copia testo'}
        </button>
      </div>
    </motion.div>
  );
};

const QuizPage = () => {
  const { lessonId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [certModal, setCertModal] = useState(false);
  const [certData, setCertData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lessons/${lessonId}`, { credentials: 'include' });
        if (res.ok) { const data = await res.json(); setLesson(data); setSelectedAnswers(new Array(data.quiz_questions?.length || 0).fill(null)); }
      } catch (error) { console.error('Error fetching lesson:', error); }
      finally { setLoading(false); }
    };
    fetchLesson();
  }, [lessonId]);

  const questions = lesson?.quiz_questions || [];

  const handleSelectAnswer = (answerIndex) => {
    if (showAnswerFeedback) return;
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
    const correct = answerIndex === questions[currentQuestion].correct_index;
    setIsCorrect(correct);
    setShowAnswerFeedback(true);
    setTimeout(() => {
      setShowAnswerFeedback(false);
      setIsCorrect(null);
      if (currentQuestion < questions.length - 1) setCurrentQuestion(currentQuestion + 1);
    }, 1200);
  };

  const handleSubmit = async () => {
    if (selectedAnswers.includes(null)) { toast.error('Rispondi a tutte le domande prima di inviare'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/quiz/submit`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ lesson_id: lessonId, answers: selectedAnswers }) });
      const data = await res.json();
      if (data.error === 'daily_limit') { toast.error(data.message); navigate('/profilo'); return; }
      setResults(data);
      setShowResults(true);
      refreshUser();
    } catch (error) { toast.error('Errore nell\'invio del quiz'); }
    finally { setSubmitting(false); }
  };

  const handleRetry = () => { setSelectedAnswers(new Array(questions.length).fill(null)); setCurrentQuestion(0); setShowResults(false); setResults(null); };

  const getMotivationalMessage = (score) => {
    if (lesson?.is_level_quiz) {
      const nextLevel = lesson.level + 1;
      if (score >= 60) return { emoji: '🎉', message: nextLevel <= 4 ? `Quiz superato! Livello ${nextLevel} sbloccato!` : 'Percorso completato! Sei un esperto SAPIENS!' };
      return { emoji: '📚', message: 'Non hai raggiunto il 60%. Ripassala lezione e riprova!' };
    }
    if (score >= 100) return { emoji: '🏆', message: 'Perfetto! Sei un vero esperto!' };
    if (score >= 80) return { emoji: '🎉', message: 'Ottimo lavoro! Hai una solida comprensione!' };
    if (score >= 60) return { emoji: '👍', message: 'Buon risultato! Continua così!' };
    if (score >= 40) return { emoji: '📚', message: 'Rileggi la lezione e riprova!' };
    return { emoji: '💪', message: 'Non mollare! La pratica rende perfetti!' };
  };

  const isLevelQuizPassed = lesson?.is_level_quiz && results?.score >= 60;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]"><div className="w-12 h-12 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!lesson || questions.length === 0) return <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]"><div className="text-center"><p className="text-lg text-[#888780] mb-4">Nessun quiz disponibile</p><Button onClick={() => navigate('/percorsi')} className="sapiens-btn-primary">Torna ai percorsi</Button></div></div>;

  if (showResults && results) {
    const { emoji, message } = getMotivationalMessage(results.score);
    const scoreColor = results.score >= 80 ? 'text-[#3D7A52]' : results.score >= 60 ? 'text-[#C9A84C]' : 'text-[#F09595]';
    return (
      <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="quiz-results">
        <div className="max-w-[600px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="sapiens-card p-8 text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }} className="mb-6"><span className="text-6xl">{emoji}</span></motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className={`text-7xl font-medium ${scoreColor} mb-4`}><AnimatedScore value={results.score} /></motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
              <h2 className="text-2xl font-medium text-[#E8E8E4] mb-2">{message}</h2>
              <p className="text-[#888780]">{results.correct} risposte corrette su {results.total}</p>
              {isLevelQuizPassed && lesson.level < 4 && (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(61,122,82,0.15)', border: '1px solid rgba(61,122,82,0.4)', color: '#3D7A52' }}>
                  <span className="font-semibold">Livello {lesson.level + 1} sbloccato!</span>
                </div>
              )}
              {isLevelQuizPassed && lesson.level === 4 && (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C' }}>
                  <span className="font-semibold">Percorso completato! Sei un esperto SAPIENS!</span>
                </div>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="space-y-3 my-8 text-left">
              {results.results.map((result, index) => (
                <div key={index} className={`p-4 rounded-xl border ${result.is_correct ? 'bg-[#3D7A52]/10 border-[#3D7A52]/30' : 'bg-[#F09595]/10 border-[#F09595]/30'}`}>
                  <div className="flex items-start gap-3">{result.is_correct ? <CheckCircle className="w-5 h-5 text-[#3D7A52] flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[#F09595] flex-shrink-0 mt-0.5" />}<div><p className="text-[#E8E8E4] font-medium">{result.question}</p>{!result.is_correct && <p className="text-sm text-[#888780] mt-1">Risposta corretta: <span className="text-[#3D7A52]">{questions[index]?.options[result.correct_answer]}</span></p>}</div></div>
                </div>
              ))}
            </motion.div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {results.score < 100 && <Button className="sapiens-btn-secondary" onClick={handleRetry} data-testid="retry-quiz-btn"><RotateCcw className="w-4 h-4 mr-2" />Riprova</Button>}
              <Button onClick={() => navigate('/percorsi')} className="sapiens-btn-primary" data-testid="back-to-lessons-btn"><Home className="w-4 h-4 mr-2" />Torna ai percorsi</Button>
              {isLevelQuizPassed && (
                <button
                  onClick={async () => {
                    if (!lesson) return;
                    try {
                      const LEVEL_NAMES = { 1: 'Livello 1 — Base', 2: 'Livello 2 — Intermedio', 3: 'Livello 3 — Avanzato', 4: 'Livello 4 — Esperto' };
                      const res = await fetch(`${API_URL}/api/certificates`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                        body: JSON.stringify({ level: lesson.level, level_name: LEVEL_NAMES[lesson.level] || `Livello ${lesson.level}` }),
                      });
                      if (res.ok) { const cert = await res.json(); setCertData(cert); }
                    } catch {}
                    setCertModal(true);
                  }}
                  data-testid="download-certificate-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{ border: '1px solid rgba(201,168,76,0.6)', color: '#C9A84C', background: 'rgba(201,168,76,0.08)' }}
                >
                  <Award className="w-4 h-4" />
                  Scarica Certificato
                </button>
              )}
            </div>
            <ShareButtons score={results.score} lesson={lesson} />
          </motion.div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const optionLabels = ['A', 'B', 'C', 'D'];

  return (
    <>
      <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="quiz-page">
        <div className="max-w-[600px] mx-auto px-4">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2"><span className="text-sm text-[#888780]">Domanda {currentQuestion + 1} di {questions.length}</span><span className="font-medium tabular-nums text-[#E8E8E4]">{Math.round(progress)}%</span></div>
            <ProgressBar value={progress} size="small" />
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={currentQuestion} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="sapiens-card p-8">
              <h2 className="text-xl font-medium text-[#E8E8E4] mb-8">{currentQ.question}</h2>
              <div className="space-y-3">
                {currentQ.options.map((option, index) => {
                  const isSelected = selectedAnswers[currentQuestion] === index;
                  const isCorrectAnswer = index === currentQ.correct_index;
                  const showCorrect = showAnswerFeedback && isCorrectAnswer;
                  const showWrong = showAnswerFeedback && isSelected && !isCorrectAnswer;
                  return (
                    <motion.button key={index} onClick={() => handleSelectAnswer(index)} disabled={showAnswerFeedback} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${showCorrect ? 'border-[#3D7A52] bg-[#3D7A52]/15' : showWrong ? 'border-[#F09595] bg-[#F09595]/15' : isSelected ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-white/[0.08] hover:border-[#3D7A52]/50 hover:bg-white/[0.02]'}`} animate={showCorrect ? { scale: [1, 1.02, 1] } : showWrong ? { x: [0, -5, 5, -5, 0] } : {}} transition={{ duration: 0.3 }} data-testid={`quiz-option-${index}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-colors ${showCorrect ? 'border-[#3D7A52] bg-[#3D7A52] text-white' : showWrong ? 'border-[#F09595] bg-[#F09595] text-white' : isSelected ? 'border-[#C9A84C] bg-[#C9A84C] text-[#3A2800]' : 'border-white/20 text-[#888780]'}`}>{showCorrect ? <CheckCircle className="w-5 h-5" /> : showWrong ? <XCircle className="w-5 h-5" /> : optionLabels[index]}</div>
                        <span className="text-[#E8E8E4] flex-1">{option}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-between mt-8">
            <Button className="sapiens-btn-secondary" onClick={() => currentQuestion > 0 && setCurrentQuestion(currentQuestion - 1)} disabled={currentQuestion === 0 || showAnswerFeedback} data-testid="prev-question-btn">Precedente</Button>
            {currentQuestion === questions.length - 1 ? (
              <Button onClick={handleSubmit} disabled={submitting || selectedAnswers.includes(null) || showAnswerFeedback} className="sapiens-btn-primary" data-testid="submit-quiz-btn">{submitting ? 'Invio...' : 'Invia risposte'}</Button>
            ) : (
              <Button onClick={() => setCurrentQuestion(currentQuestion + 1)} disabled={selectedAnswers[currentQuestion] === null || showAnswerFeedback} className="sapiens-btn-primary" data-testid="next-question-btn">Successiva<ChevronRight className="w-4 h-4 ml-2" /></Button>
            )}
          </div>
          <div className="flex justify-center gap-2 mt-8">
            {questions.map((_, index) => (<button key={index} onClick={() => !showAnswerFeedback && setCurrentQuestion(index)} disabled={showAnswerFeedback} className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentQuestion ? 'bg-[#C9A84C] scale-125' : selectedAnswers[index] !== null ? 'bg-[#3D7A52]' : 'bg-white/20'}`} data-testid={`question-indicator-${index}`} />))}
          </div>
        </div>
      </div>
      {certModal && (
        <CertificateModal
          isOpen={certModal}
          onClose={() => setCertModal(false)}
          level={lesson?.level}
          levelName={certData?.level_name || `Livello ${lesson?.level}`}
          userName={user?.name || certData?.user_name}
          issuedAt={certData?.issued_at}
          verificationCode={certData?.verification_code}
          onDownload={() => {}}
        />
      )}
    </>
  );
};

export default QuizPage;
